import { ChevronDown, Search, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useDebounced } from '../record-table/use-debounced.ts';
import { Button } from '../ui/button.tsx';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../ui/dropdown-menu.tsx';

/**
 * A task page's filter, drawn like the record lists' filter chips (`FieldChip` in
 * record-table/quick-filters.tsx): outlined until it narrows the list, then filled with a remove
 * button. The task pages filter through the tasks API's own parameters, not filter conditions,
 * so they cannot use `QuickFilters` itself.
 *
 * Given its items as a function of `match`, the menu opens with a search field: as the record
 * lists' free-text filters, it narrows only from the 3rd character, 300 ms after typing stops.
 */
const MIN_CHARS = 3;
export function TaskFilterChip({
  name,
  label,
  active,
  onClear,
  testId,
  children,
}: {
  /** The filter's name, for the remove button ("Remove Status filter"). */
  name: string;
  label: string;
  active: boolean;
  onClear: () => void;
  testId: string;
  /** The menu's items; as a function, the menu searches and `match(label)` says which to show. */
  children: ReactNode | ((match: (label: string) => boolean) => ReactNode);
}) {
  const [typed, setTyped] = useState('');
  const debounced = useDebounced(typed.trim().toLowerCase(), 300);
  const q = debounced.length >= MIN_CHARS ? debounced : '';
  const match = (label: string) => !q || label.toLowerCase().includes(q);
  return (
    <span className="inline-flex items-center">
      <DropdownMenu onOpenChange={(open) => !open && setTyped('')}>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant={active ? 'default' : 'outline'}
            data-active={active}
            data-testid={testId}
            className={active ? 'max-w-64 rounded-r-none' : 'max-w-64'}
            title={label}
          >
            <span className="truncate">{label}</span>
            <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
          {typeof children === 'function' ? (
            <>
              <label className="border-border mb-1 flex items-center gap-1.5 border-b px-2 pb-1.5">
                <Search className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
                <input
                  autoFocus
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  // The menu's own keys (type-ahead, Space/Enter on an item) must not take the
                  // letters. The menu moves only between its items, so ArrowDown steps into them;
                  // Escape still closes it.
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      e.currentTarget
                        .closest('[role="menu"]')
                        ?.querySelector<HTMLElement>('[role^="menuitem"]')
                        ?.focus();
                    } else if (e.key !== 'Escape') e.stopPropagation();
                  }}
                  placeholder="Search…"
                  aria-label={`Search ${name}`}
                  className="placeholder:text-muted-foreground w-40 bg-transparent text-sm outline-none"
                />
              </label>
              {children(match)}
            </>
          ) : (
            children
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {active && (
        <Button
          size="icon-sm"
          variant="default"
          className="rounded-l-none"
          aria-label={`Remove ${name} filter`}
          onClick={onClear}
        >
          <X />
        </Button>
      )}
    </span>
  );
}

/** How many tasks the filters found, where a record list says how many rows it loaded. */
export function TaskCount({ count }: { count: number }) {
  return (
    <span className="text-muted-foreground text-sm" data-testid="task-count">
      {count.toLocaleString()} task{count === 1 ? '' : 's'}
    </span>
  );
}
